import { Sort } from '@material-ui/icons'
import axios from 'axios'
import {
  BASE_URL,
  BASE_ALERTS_URL,
  JWT_TOKEN,
  RELOGIN_PARAM,
  API_V5,
  ACCOUNT_SUSPENDED_PATH,
  AUTH_REFRESH,
} from 'lib/constants'
import { Error, RestParamsV5, SortOrder } from 'lib/types'
import { isTokenNearingExpiry } from 'lib/utils/token'
import { pusher } from 'models/api'
import { refreshAuthToken } from 'models/api/refreshToken'
import { clearLocalStorage } from 'models/ThemeMode'

// The API returns this in a 401's error code when the JWT is merely expired, as
// opposed to structurally invalid or tied to a missing user. Only this one is
// worth retrying — refreshing on any other 401 would loop.
const EXPIRED_JWT_CODE = 40101

declare module 'axios' {
  export interface AxiosResponse<T = any> extends Promise<T> {}

  export interface AxiosRequestConfig {
    handleSuspendedOrg?: boolean
    _retry?: boolean
  }
}

export interface ApiResponseV5 {
  data: any // Define the actual type of 'data' here
  meta: any // Define the actual type of 'meta' here
}

const API = axios.create({
  baseURL: BASE_URL,
})

export const AxiosInstanceForMocking = API

API.interceptors.request.use(async (config: any) => {
  const isRefreshCall = config.url?.includes(AUTH_REFRESH)
  if (!isRefreshCall && isTokenNearingExpiry()) {
    await refreshAuthToken()
  }

  const token = localStorage.getItem(JWT_TOKEN)
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

/***  function for retrying failed api calls - revisit ***/
// const sleepRequest = (milliseconds: any, originalRequest: any) => {
//   return new Promise((resolve, reject) => {
//     setTimeout(() => resolve(API(originalRequest)), milliseconds)
//   })
// }
// useful link: https://gist.github.com/edmondburnett/38ed3451de659dc43fa3f24befc0073b

API.interceptors.response.use(
  res => res,
  async err => {
    const status = err?.response?.status
    const shouldHandleSuspended = err?.config?.handleSuspendedOrg

    if (status === 401) {
      const errors: Array<Error> = err.response.data?.errors || []
      const isExpired = errors.some(e => Number(e?.code) === EXPIRED_JWT_CODE)

      if (isExpired && err.config && !err.config._retry) {
        const token = await refreshAuthToken()
        if (token) {
          err.config._retry = true
          err.config.headers = { ...err.config.headers, Authorization: `Bearer ${token}` }
          return API(err.config)
        }
      }

      logout(errors)
      return Promise.reject(err)
    }

    if (status === 403 && shouldHandleSuspended) {
      if (window.location.pathname !== ACCOUNT_SUSPENDED_PATH) {
        window.location.href = ACCOUNT_SUSPENDED_PATH
      }
      return Promise.reject(err)
    }

    return Promise.reject(err)
  },
)

function logout(errors: Array<Error>) {
  const code = errors[0]?.code || 40100
  clearLocalStorage()
  pusher?.disconnect()
  window.location.href = `/?${RELOGIN_PARAM}=${code}`
}

export const putRequest = ({ endpoint, data = {}, header }: { endpoint: string; data?: any; header?: any }) =>
  API.put(`${BASE_URL}${endpoint}`, data, { headers: header })

export const postRequest = ({ endpoint, data = {}, header }: { endpoint: string; data: any; header?: any }) =>
  API.post(`${BASE_URL}${endpoint}`, data, { headers: header })

export const postAlertRequest = ({ endpoint, data = {}, header }: { endpoint: string; data: any; header?: any }) =>
  API.post(`${BASE_ALERTS_URL}${endpoint}`, data, { headers: header })

export const getRequest = ({
  endpoint,
  header = {},
  handleSuspendedOrg = false,
}: {
  endpoint: string
  header?: any
  handleSuspendedOrg?: boolean
}) =>
  API.get(`${BASE_URL}${endpoint}`, {
    headers: header,
    handleSuspendedOrg,
  })

export const getRequestV5 = <FVE, SE>({ endpoint, handleSuspendedOrg = false, headers = {}, params = {} }: RestParamsV5<FVE, SE>) => {
  // let filters ? { params?.filters } : {}; // Create params object with filters if defined

  let finalEndpoint = endpoint + '?'
  if (params.filterBy) {
    finalEndpoint += `filter[${params.filterBy.field}]=${params.filterBy.value}&`
  }
  if (params.searchBy?.value) {
    finalEndpoint += `search=${params.searchBy.value}&`
  }
  if (params.sortBy) {
    switch (params.sortBy.order) {
      case SortOrder.Ascending:
        finalEndpoint += `sort=${params.sortBy.field}`
        break
      case SortOrder.Descending:
        finalEndpoint += `sort=-${params.sortBy.field}`
        break
      default:
        console.log('No sort order applied')
        break
    }
  }
  // Add pagination params
  if (params?.pagination) {
    finalEndpoint += `&page=${params.pagination.currentPage}&`
  }

  if (params?.custom) {
    for (const [key, val] of Object.entries(params.custom)) {
      finalEndpoint += `&${key}=${val}`
    }
  }
  console.log('endpoint: ', `${BASE_URL}${API_V5}${finalEndpoint}`)
  console.log('headers: ', headers)

  return API.get<any>(`${BASE_URL}${API_V5}${finalEndpoint}`, {
    headers: headers,
    handleSuspendedOrg
  })
}

export const deleteRequest = ({
  endpoint,
  header,
  data,
}: {
  endpoint: string
  header?: any
  data?: any
}) =>
  API.delete(`${BASE_URL}${endpoint}`, {
    headers: header,
    data,
  })